import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "relative isolate inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border text-sm font-semibold shadow-sm transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-studio-700 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Primary - main action button
        default: "border-studio-800/20 bg-studio-900 text-white hover:bg-studio-800 active:bg-studio-950",
        // Destructive - danger actions
        destructive: "border-red-700/20 bg-red-600 text-white hover:bg-red-700 active:bg-red-800",
        // Outline - secondary actions
        outline: "border-cw-strong bg-cw-card text-slate-800 hover:border-slate-400 hover:bg-cw-subtle",
        // Secondary - muted actions
        secondary: "border-cw bg-cw-subtle text-slate-800 hover:bg-cw-muted active:bg-cw-muted",
        // Ghost - minimal style
        ghost: "border-transparent bg-transparent text-slate-600 shadow-none hover:bg-cw-subtle hover:text-slate-950",
        // Link - text only
        link: "border-transparent bg-transparent text-studio-800 shadow-none underline-offset-4 hover:text-studio-900 hover:underline",
        // Studio - brand primary (slightly elevated)
        studio: "border-studio-800/20 bg-studio-900 text-white hover:bg-studio-800 active:bg-studio-950",
        // Aquamarine - brand secondary
        aquamarine: "border-cyan-800/20 bg-cyan-700 text-white hover:bg-cyan-800 active:bg-cyan-900",
        // Success - positive actions
        success: "border-emerald-800/20 bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800",
        // Soft variants - subtle backgrounds
        soft: "border-cw bg-cw-subtle text-slate-700 hover:bg-cw-muted",
        "soft-studio": "border-studio-200 bg-studio-50 text-studio-900 hover:bg-studio-100",
        "soft-aquamarine": "border-cyan-200 bg-cyan-50 text-cyan-800 hover:bg-cyan-100",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-lg px-3",
        lg: "h-11 rounded-lg px-8",
        xl: "h-12 rounded-lg px-10 text-base",
        icon: "h-10 w-10",
        "icon-sm": "h-8 w-8",
        "icon-lg": "h-12 w-12",
      },
      rounded: {
        default: "",
        full: "rounded-full",
        xl: "rounded-xl",
        "2xl": "rounded-2xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      rounded: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /** Whether to show loading state */
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, rounded, asChild = false, loading = false, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, rounded, className }))}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {children}
          </>
        ) : children}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
